/**
 * QR Verification Page
 * Public page for consumers to scan and verify product QR codes
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { qrService, ProductInfo, ConsumerData } from '../services/qr.service';
import { packagingService } from '../services/packaging.service';
import { Button, Input } from '../components/ui';
import ConsumerDataForm from '../components/ConsumerDataForm';
import ProductImageGallery from '../components/ProductImageGallery';
import NutritionFactsDisplay from '../components/NutritionFactsDisplay';
import CustomContentDisplay from '../components/CustomContentDisplay';
import ProgressiveContentLoader from '../components/ProgressiveContentLoader';

const QRVerificationPage: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const tokenFromQuery = searchParams.get('token');
  const navigate = useNavigate();
  
  const [qrToken, setQrToken] = useState(token || tokenFromQuery || '');
  const [productInfo, setProductInfo] = useState<ProductInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConsumerForm, setShowConsumerForm] = useState(false);
  const [consumptionLoading, setConsumptionLoading] = useState(false);
  const [enhancedContentLoading, setEnhancedContentLoading] = useState(false);
  const [enhancedContentError, setEnhancedContentError] = useState<string | null>(null);
  const [geoStatus, setGeoStatus] = useState<
    'unknown' | 'prompt' | 'granted' | 'denied' | 'unavailable' | 'skipped'
  >('unknown');
  const [geoLocation, setGeoLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [geoMessage, setGeoMessage] = useState<string | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);

  const requestLocation = (source: 'auto' | 'user' | 'retry') => {
    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      if (source !== 'auto') {
        setGeoMessage('Location is not supported on this device.');
      }
      return;
    }

    setGeoLoading(true);
    if (source !== 'auto') {
      setGeoMessage(null);
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeoLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setGeoStatus('granted');
        if (source !== 'auto') {
          setGeoMessage('Location enabled. Thanks!');
        }
        setGeoLoading(false);
      },
      (error) => {
        if (error.code === 1) {
          setGeoStatus('denied');
          if (source !== 'auto') {
            setGeoMessage('Location permission blocked. You can retry anytime.');
          }
        } else if (error.code === 2) {
          setGeoStatus('unavailable');
          if (source !== 'auto') {
            setGeoMessage('Location is unavailable right now.');
          }
        } else {
          setGeoStatus('unavailable');
          if (source !== 'auto') {
            setGeoMessage('Location request timed out. Please retry.');
          }
        }
        setGeoLoading(false);
      },
      { timeout: 6000, maximumAge: 300000 }
    );
  };

  // Enhanced content retry function
  const retryEnhancedContent = async () => {
    if (!productInfo || productInfo.qrType !== 'inner') return;
    
    try {
      setEnhancedContentLoading(true);
      setEnhancedContentError(null);
      
      // Re-verify to get enhanced content
      const info = await qrService.verifyQR(qrToken, geoLocation || undefined);
      
      if (info.enhancedInfo) {
        setProductInfo(info);
        setEnhancedContentLoading(false);
      } else {
        setEnhancedContentError('Enhanced content not available for this product');
        setEnhancedContentLoading(false);
      }
    } catch (error) {
      setEnhancedContentError('Failed to load enhanced content');
      setEnhancedContentLoading(false);
    }
  };

  useEffect(() => {
    let isActive = true;

    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      return () => {
        isActive = false;
      };
    }

    const handlePermissionState = (state: PermissionState) => {
      if (!isActive) return;
      if (state === 'granted') {
        setGeoStatus('granted');
        if (!geoLocation) {
          requestLocation('auto');
        }
      } else if (state === 'denied') {
        setGeoStatus('denied');
      } else {
        setGeoStatus('prompt');
      }
    };

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((result) => {
          handlePermissionState(result.state);
          result.onchange = () => handlePermissionState(result.state);
        })
        .catch(() => {
          setGeoStatus('prompt');
        });
    } else {
      setGeoStatus('prompt');
    }

    return () => {
      isActive = false;
    };
  }, [geoLocation]);

  // Auto-verify if token is in URL
  useEffect(() => {
    if (token || tokenFromQuery) {
      handleVerify();
    }
  }, [token, tokenFromQuery]);

  const handleVerify = async () => {
    if (!qrToken.trim()) {
      setError('Please enter a QR code');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setProductInfo(null);

      // First, check if this is a package QR
      try {
        const packageResult = await packagingService.getPackageByQR(qrToken);
        if (packageResult.success && packageResult.data) {
          // This is a package QR, redirect to package verification page
          navigate(`/package/${qrToken}`);
          return;
        }
      } catch (pkgErr) {
        // Not a package, continue with product verification
      }

      // Verify QR code as product
      const info = await qrService.verifyQR(qrToken, geoLocation || undefined);
      setProductInfo(info);
      
      // Handle enhanced content loading states
      if (info.qrType === 'inner') {
        if (info.enhancedInfo) {
          // Enhanced info is available and loaded
          setEnhancedContentLoading(false);
          setEnhancedContentError(null);
        } else {
          // Inner QR but no enhanced info - this is normal for basic products
          setEnhancedContentLoading(false);
          setEnhancedContentError(null);
        }
      } else {
        // Outer QR - no enhanced content expected
        setEnhancedContentLoading(false);
        setEnhancedContentError(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleConsume = () => {
    setShowConsumerForm(true);
  };

  const handleConsumerDataSubmit = async (consumerData: ConsumerData) => {
    try {
      setConsumptionLoading(true);
      setError(null);

      // Mark as consumed
      const result = await qrService.markAsConsumed(qrToken, consumerData, geoLocation || undefined);

      if (result.success) {
        // Refresh product info
        await handleVerify();
        setShowConsumerForm(false);
      } else {
        setError(result.error || 'Failed to mark as consumed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to mark as consumed');
    } finally {
      setConsumptionLoading(false);
    }
  };

  const handleConsumerDataSkip = async () => {
    try {
      setConsumptionLoading(true);
      setError(null);

      // Mark as consumed without consumer data
      const result = await qrService.markAsConsumed(qrToken, undefined, geoLocation || undefined);

      if (result.success) {
        // Refresh product info
        await handleVerify();
        setShowConsumerForm(false);
      } else {
        setError(result.error || 'Failed to mark as consumed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to mark as consumed');
    } finally {
      setConsumptionLoading(false);
    }
  };

  const showGeoBanner = geoStatus !== 'granted';
  let geoTitle = 'Enable location for better authenticity checks';
  let geoBody =
    'We use approximate location to improve fraud detection and scan maps. You can skip anytime.';
  let showRetry = false;
  let showSkip = true;

  if (geoStatus === 'denied') {
    geoTitle = 'Location access blocked';
    geoBody = 'Enable location to improve authenticity checks and scan maps.';
    showRetry = true;
  } else if (geoStatus === 'unavailable') {
    geoTitle = 'Location unavailable';
    geoBody = 'We could not access your location. You can retry or continue without it.';
    showRetry = true;
  } else if (geoStatus === 'skipped') {
    geoTitle = 'Location disabled';
    geoBody = 'Enable location anytime for better scan maps and fraud insights.';
    showSkip = false;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 text-gray-900">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 rounded-lg p-2">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Pureaum</h1>
              <p className="text-sm text-gray-600">Product Verification</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {showGeoBanner && (
          <div className="bg-white rounded-lg border border-blue-100 shadow-sm p-4 mb-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">{geoTitle}</p>
                <p className="text-sm text-gray-600 mt-1">{geoBody}</p>
                {geoMessage && <p className="text-xs text-gray-500 mt-1">{geoMessage}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => requestLocation(showRetry ? 'retry' : 'user')}
                  disabled={geoLoading}
                >
                  {geoLoading ? 'Requesting...' : showRetry ? 'Retry' : 'Enable location'}
                </Button>
                {showSkip && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setGeoStatus('skipped');
                      setGeoMessage('Location skipped. You can enable it later.');
                    }}
                    disabled={geoLoading}
                  >
                    Skip
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Scanner Interface */}
        {!productInfo && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Product</h2>
              <p className="text-gray-600">
                Enter your QR code to verify authenticity and view product information
              </p>
            </div>

            {/* Manual Entry */}
            <div className="max-w-md mx-auto">
              <label htmlFor="qrToken" className="block text-sm font-medium text-gray-700 mb-2">
                QR Code Token
              </label>
              <div className="flex gap-3">
                <Input
                  id="qrToken"
                  type="text"
                  value={qrToken}
                  onChange={(e) => setQrToken(e.target.value)}
                  placeholder="Enter QR code or scan"
                  className="flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
                />
                <Button onClick={handleVerify} disabled={loading || !qrToken.trim()}>
                  {loading ? 'Verifying...' : 'Verify'}
                </Button>
              </div>

              {/* Camera Scanner Placeholder */}
              <div className="mt-6 p-6 border-2 border-dashed border-gray-300 rounded-lg text-center">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-sm text-gray-600">Camera scanner coming soon</p>
                <p className="text-xs text-gray-500 mt-1">For now, please enter the QR code manually</p>
              </div>
            </div>

            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>
        )}

        {/* Product Information Display */}
        {productInfo && !showConsumerForm && (
          <div className="space-y-6">
            {/* Risk-Based UI Messages for Outer QR */}
            {productInfo.qrType === 'outer' && productInfo.uiGuidance && (
              <div className={`border-l-4 p-6 rounded-lg ${
                productInfo.uiGuidance.type === 'info' ? 'bg-blue-50 border-blue-400' :
                productInfo.uiGuidance.type === 'warning' ? 'bg-yellow-50 border-yellow-400' :
                'bg-orange-50 border-orange-400'
              }`}>
                <div className="flex items-center gap-3">
                  {productInfo.uiGuidance.type === 'info' && (
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  )}
                  {productInfo.uiGuidance.type === 'warning' && (
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  {productInfo.uiGuidance.type === 'alert' && (
                    <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold ${
                      productInfo.uiGuidance.type === 'info' ? 'text-blue-900' :
                      productInfo.uiGuidance.type === 'warning' ? 'text-yellow-900' :
                      'text-orange-900'
                    }`}>
                      {productInfo.uiGuidance.title}
                    </h3>
                    <p className={`text-sm mt-1 ${
                      productInfo.uiGuidance.type === 'info' ? 'text-blue-800' :
                      productInfo.uiGuidance.type === 'warning' ? 'text-yellow-800' :
                      'text-orange-800'
                    }`}>
                      {productInfo.uiGuidance.message}
                    </p>
                    {productInfo.uiGuidance.showStepUp && productInfo.uiGuidance.ctaText && (
                      <div className="mt-3">
                        <button className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                          productInfo.uiGuidance.emphasis 
                            ? 'bg-orange-600 text-white hover:bg-orange-700' 
                            : 'bg-yellow-600 text-white hover:bg-yellow-700'
                        }`}>
                          {productInfo.uiGuidance.ctaText}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Activation Status Banner */}
            {productInfo.product.activationStatus === 'CREATED' && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold text-yellow-900">Not Yet Available</h3>
                    <p className="text-sm text-yellow-800 mt-1">{productInfo.message}</p>
                  </div>
                </div>
              </div>
            )}

            {productInfo.product.activationStatus === 'CONSUMED' && (
              <div className="bg-gray-50 border-l-4 border-gray-400 p-6 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Already Consumed</h3>
                    <p className="text-sm text-gray-700 mt-1">{productInfo.message}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Standard verification message for inner QR or when no risk guidance */}
            {productInfo.product.activationStatus === 'ACTIVATED' && 
             (!productInfo.uiGuidance || productInfo.qrType === 'inner') && (
              <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold text-green-900">Authentic Product Verified!</h3>
                    <p className="text-sm text-green-800 mt-1">{productInfo.message}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Fraud Warnings */}
            {productInfo.warnings && productInfo.warnings.length > 0 && (
              <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold text-red-900">Warning!</h3>
                    {productInfo.warnings.map((warning, index) => (
                      <p key={index} className="text-sm text-red-800 mt-1">{warning}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Anomaly Detection Information (for transparency) */}
            {productInfo.anomalyDetection && productInfo.qrType === 'outer' && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Security Analysis
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Risk Level:</span>
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                      productInfo.anomalyDetection.riskLevel === 'low' ? 'bg-green-100 text-green-800' :
                      productInfo.anomalyDetection.riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {productInfo.anomalyDetection.riskLevel.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Risk Score:</span>
                    <span className="ml-2 font-medium">{productInfo.anomalyDetection.riskScore}/100</span>
                  </div>
                </div>
                {productInfo.anomalyDetection.recommendStepUp && (
                  <div className="mt-2 text-xs text-gray-600">
                    ℹ️ Enhanced verification recommended based on scan pattern analysis
                  </div>
                )}
              </div>
            )}

            {productInfo.qrType === 'outer' &&
              productInfo.enhancedInfo?.manufacturingDetails?.mrp !== undefined &&
              productInfo.enhancedInfo?.manufacturingDetails?.mrp !== null && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-6 py-4">
                <div className="text-sm font-medium text-amber-800">MRP (INR)</div>
                <div className="text-2xl font-bold text-amber-900">
                  {Number(productInfo.enhancedInfo.manufacturingDetails.mrp).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </div>
              </div>
            )}

            {/* Product Details */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Product Information</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-600">Product Name</dt>
                  <dd className="text-lg font-semibold text-gray-900 mt-1">{productInfo.product.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Serial Number</dt>
                  <dd className="text-lg font-semibold text-gray-900 mt-1">{productInfo.product.serialNumber}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">
                    {productInfo.enhancedInfo?.productType === 'produce' ? 'Grower' : 'Manufacturer'}
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900 mt-1">{productInfo.product.manufacturer}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Status</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      productInfo.product.activationStatus === 'ACTIVATED' ? 'bg-green-100 text-green-800' :
                      productInfo.product.activationStatus === 'CONSUMED' ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {productInfo.product.activationStatus}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">QR Type</dt>
                  <dd className="text-lg font-semibold text-gray-900 mt-1">
                    {productInfo.qrType === 'outer' ? 'Outer QR (Pre-Purchase)' : 'Inner QR (Post-Purchase)'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Scan Count</dt>
                  <dd className="text-lg font-semibold text-gray-900 mt-1">{productInfo.scanCount}</dd>
                </div>
              </dl>
            </div>

            {/* Supply Chain Journey */}
            {productInfo.supplyChain && productInfo.supplyChain.length > 0 && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Supply Chain Journey</h3>
                <div className="space-y-4">
                  {productInfo.supplyChain.map((transfer, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-blue-600">{index + 1}</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">{transfer.from}</span>
                          {' → '}
                          <span className="font-medium">{transfer.to}</span>
                        </p>
                        <p className="text-xs text-gray-500">{new Date(transfer.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Enhanced Product Information - show when available */}
            {productInfo?.enhancedInfo && (
              <div className="space-y-6">
                {/* Enhanced Features Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Enhanced Product Information Available
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {productInfo.enhancedInfo.images && productInfo.enhancedInfo.images.length > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        📸 {productInfo.enhancedInfo.images.length} Image{productInfo.enhancedInfo.images.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {productInfo.enhancedInfo.nutritionInfo && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        🥗 Nutrition Facts
                      </span>
                    )}
                    {productInfo.enhancedInfo.customContent?.ingredients && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        📋 Ingredients
                      </span>
                    )}
                    {productInfo.enhancedInfo.customContent?.allergens && productInfo.enhancedInfo.customContent.allergens.length > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        ⚠️ Allergen Info
                      </span>
                    )}
                    {productInfo.enhancedInfo.productType === 'produce' && productInfo.enhancedInfo.produceDetails && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                         Harvest Details
                      </span>
                    )}

                    {productInfo.enhancedInfo.productType !== 'produce' && productInfo.enhancedInfo.manufacturingDetails && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        🏭 Manufacturing Details
                      </span>
                    )}
                    {(productInfo.enhancedInfo.customContent?.usageInstructions || 
                      productInfo.enhancedInfo.customContent?.storageInstructions ||
                      productInfo.enhancedInfo.customContent?.customMessage ||
                      productInfo.enhancedInfo.customContent?.brandStory) && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        💬 Additional Information
                      </span>
                    )}
                  </div>
                </div>
                {/* Product Images */}
                {productInfo.enhancedInfo.images && productInfo.enhancedInfo.images.length > 0 ? (
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Product Images</h3>
                    <ProgressiveContentLoader
                      loading={enhancedContentLoading}
                      error={enhancedContentError}
                      retryAction={retryEnhancedContent}
                      skeletonHeight="h-64"
                    >
                      <ProductImageGallery
                        images={productInfo.enhancedInfo.images}
                        productName={productInfo.product.name}
                      />
                    </ProgressiveContentLoader>
                  </div>
                ) : (
                  // Show placeholder when enhanced info exists but no images
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <div className="text-gray-400 mb-3">
                      <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-600 mb-2">No Product Images</h4>
                    <p className="text-sm text-gray-500">
                      No images have been uploaded for this product.
                    </p>
                  </div>
                )}

                {/* Nutrition Facts */}
                {productInfo.enhancedInfo.nutritionInfo && (
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Nutrition Information</h3>
                    <ProgressiveContentLoader
                      loading={enhancedContentLoading}
                      error={enhancedContentError}
                      retryAction={retryEnhancedContent}
                      skeletonHeight="h-96"
                    >
                      <div className="flex justify-center">
                        <NutritionFactsDisplay
                          nutritionInfo={productInfo.enhancedInfo.nutritionInfo}
                        />
                      </div>
                    </ProgressiveContentLoader>
                  </div>
                )}

                {/* Custom Content and Manufacturing Details */}
                {(productInfo.enhancedInfo.customContent || productInfo.enhancedInfo.manufacturingDetails || productInfo.enhancedInfo.produceDetails) && (
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Product Information</h3>
                    <ProgressiveContentLoader
                      loading={enhancedContentLoading}
                      error={enhancedContentError}
                      retryAction={retryEnhancedContent}
                      skeletonHeight="h-48"
                    >
                      <CustomContentDisplay
                        productType={productInfo.enhancedInfo.productType}
                        customContent={productInfo.enhancedInfo.customContent}
                        manufacturingDetails={productInfo.enhancedInfo.productType === 'produce' ? undefined : productInfo.enhancedInfo.manufacturingDetails}
                        produceDetails={productInfo.enhancedInfo.produceDetails}
                      />
                    </ProgressiveContentLoader>
                  </div>
                )}
              </div>
            )}

            {/* Enhanced Content Placeholder for Inner QR without enhanced info */}
            {productInfo.qrType === 'inner' && !productInfo.enhancedInfo && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <div className="text-gray-400 mb-3">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-600 mb-2">Basic Product Information</h4>
                <p className="text-sm text-gray-500">
                  This product contains basic verification information. Enhanced details like images, 
                  nutrition facts, and custom content are not available for this product.
                </p>
              </div>
            )}

            {/* Enhanced Content Hint for Outer QR */}
            {false && productInfo?.qrType === 'outer' && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 text-center">
                <div className="text-indigo-400 mb-3">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-indigo-900 mb-2">More Information Available</h4>
                <p className="text-sm text-indigo-700 mb-3">
                  This is the outer QR code for pre-purchase verification. After opening the product, 
                  scan the inner QR code to access detailed product information including:
                </p>
                <div className="flex flex-wrap justify-center gap-2 text-xs">
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 text-indigo-800">
                    📸 Product Images
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 text-indigo-800">
                    🥗 Nutrition Facts
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 text-indigo-800">
                    📋 Ingredients & Instructions
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 text-indigo-800">
                    🏭 Manufacturing Details
                  </span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  setProductInfo(null);
                  setQrToken('');
                  setError(null);
                }}
              >
                Scan Another Product
              </Button>
              
              {productInfo.canConsume && productInfo.qrType === 'inner' && (
                <Button
                  fullWidth
                  onClick={handleConsume}
                >
                  Mark as Consumed
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Consumer Data Form */}
        {showConsumerForm && (
          <ConsumerDataForm
            onSubmit={handleConsumerDataSubmit}
            onSkip={handleConsumerDataSkip}
            loading={consumptionLoading}
          />
        )}
      </main>
    </div>
  );
};

export default QRVerificationPage;
